import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useReferralDocuments } from '../../hooks/useReferralDocuments';
import { TitleText, BodyText, Button, Card, Input, Label, FileSelect } from '../ui';
import { FormField } from '../ui/FormField';
import { TextArea } from '../ui/TextArea';
import { HealthConditionsSelector } from '../ui';
import { cn } from '../../utils/cn';
import { validateEmail } from '../../utils/validation/emailValidation';
import type { PatientData, ValidationErrors, HealthCondition, LocalFileData } from '../../types/referral';
import type { ReferralDocument } from '../../services/domains/ReferralDomain';
import { getLogger } from '@/services/shared/LoggerFactory';

interface PatientDetailsStepProps {
  patientData: Partial<PatientData>;
  onDataChange: (data: Partial<PatientData>) => void;
  onNext: () => void;
  onBack: () => void;
  errors: ValidationErrors;
  isLoading?: boolean;
  referralId: string;
}

// Custom hook for patient data validation
const usePatientValidation = () => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isValidating, setIsValidating] = useState(false);
  const validationTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const validatePatientData = useCallback((data: Partial<PatientData>): ValidationErrors => {
    const errors: ValidationErrors = {};

    // Patient name validation
    if (!data.patientName?.trim()) {
      errors.patientName = 'Patient name is required';
    } else if (data.patientName.trim().length < 2) {
      errors.patientName = 'Patient name must be at least 2 characters';
    } else if (data.patientName.trim().length > 100) {
      errors.patientName = 'Patient name must be less than 100 characters';
    }


    // Email validation using utility
    if (!data.email?.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailValidation = validateEmail(data.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error;
      }
    }

    // Health conditions validation
    if (!data.conditions || data.conditions.length === 0) {
      errors.conditions = 'Please select at least one health condition';
    }

    return errors;
  }, []);

  const validateWithDebounce = useCallback((data: Partial<PatientData>, delay = 300) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    setIsValidating(true);
    validationTimeoutRef.current = setTimeout(() => {
      const errors = validatePatientData(data);
      setValidationErrors(errors);
      setIsValidating(false);
    }, delay);
  }, [validatePatientData]);

  const validateImmediately = useCallback((data: Partial<PatientData>) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    setIsValidating(true);
    const errors = validatePatientData(data);
    setValidationErrors(errors);
    setIsValidating(false);
    return errors;
  }, [validatePatientData]);

  const clearValidation = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    setValidationErrors({});
    setIsValidating(false);
  }, []);

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  return {
    validationErrors,
    isValidating,
    validateWithDebounce,
    validateImmediately,
    clearValidation
  };
};

/**
 * Step 1: Patient Details Collection
 * Complete form implementation with validation and auto-save functionality
 */
export const PatientDetailsStep: React.FC<PatientDetailsStepProps> = ({
  patientData,
  onDataChange,
  onNext,
  onBack,
  errors,
  isLoading = false,
  referralId,
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();

  // Initialize referral documents hook (only using validateFile for local validation)
  const {
    validateFile,
    error: uploadError,
  } = useReferralDocuments();

  // Note: We're now storing documents locally, no need to fetch from server

  // Local state for form fields with real-time validation
  const [localData, setLocalData] = useState<Partial<PatientData>>(patientData);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Validation hook
  const { validationErrors, isValidating, validateWithDebounce, validateImmediately } = usePatientValidation();

  // Refs for focusing error fields
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null>>({});


  // Auto-save functionality - debounced updates to parent
  useEffect(() => {
    if (isDirty) {
      const timeoutId = setTimeout(() => {
        onDataChange(localData);
        setIsDirty(false);
        setLastSaved(new Date());
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [localData, isDirty, onDataChange]);

  // Sync with parent data changes
  useEffect(() => {
    setLocalData(patientData);
  }, [patientData]);

  // Handle field changes with validation
  const handleFieldChange = useCallback((field: keyof PatientData, value: string | HealthCondition[] | string[]) => {
    // Convert string[] to HealthCondition[] for conditions field
    let processedValue = value;
    if (field === 'conditions' && Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      processedValue = value as HealthCondition[];
    }

    const newData = { ...localData, [field]: processedValue };
    setLocalData(newData);
    setIsDirty(true);

    // Trigger real-time validation with debounce
    validateWithDebounce(newData);
  }, [localData, validateWithDebounce]);

  // Convert file to base64
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64Data = result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = error => reject(error);
    });
  }, []);

  // Handle file upload - store locally instead of uploading immediately
  const handleFileUpload = useCallback(async (file: File, description?: string): Promise<ReferralDocument | null> => {
    try {
      // Convert file to base64
      const fileData = await fileToBase64(file);

      // Create local file data
      const localFileData: LocalFileData = {
        fileData,
        filename: file.name,
        mimeType: file.type,
        description,
        size: file.size
      };

      // Add to local documents array
      const currentDocuments = localData.documents || [];
      const updatedDocuments = [...currentDocuments, localFileData];

      // Update local data
      const newData = { ...localData, documents: updatedDocuments };

      setLocalData(newData);
      setIsDirty(true);

      // Return a mock ReferralDocument for compatibility with FileUpload component
      return {
        id: `local-${Date.now()}`,
        referral_id: referralId,
        original_filename: file.name,
        stored_filename: file.name,
        file_path: '',
        file_size: file.size,
        mime_type: file.type,
        document_type: file.type.split('/')[0],
        description,
        uploaded_by_user_id: '',
        created_at: new Date().toISOString()
      } as ReferralDocument;
    } catch (error) {
      logger.error('File processing failed:', error);
      return null;
    }
  }, [localData, fileToBase64, referralId]);

  // Handle file validation using the hook
  const handleFileValidation = useCallback((file: File): { isValid: boolean; error?: string } => {
    return validateFile(file);
  }, [validateFile]);

  // Focus first error field for accessibility
  const focusFirstErrorField = useCallback((errors: ValidationErrors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0];
      const fieldRef = fieldRefs.current[firstErrorField];
      if (fieldRef) {
        fieldRef.focus();
        fieldRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  // Validate and proceed to next step
  const handleNext = useCallback(() => {
    // Ensure latest data is saved
    if (isDirty) {
      onDataChange(localData);
    }

    // Perform immediate validation before navigation
    const currentErrors = validateImmediately(localData);

    // Show validation errors
    setShowValidationErrors(true);

    // If there are validation errors, prevent navigation and focus first error
    if (Object.keys(currentErrors).length > 0) {
      focusFirstErrorField(currentErrors);
      return;
    }

    // If validation passes, proceed to next step
    onNext();
  }, [isDirty, localData, onDataChange, validateImmediately, focusFirstErrorField, onNext]);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <TitleText level={2} className="mb-2">
          Patient Details
        </TitleText>
        <BodyText variant="secondary">
          Enter your patient's information and select their health conditions.
        </BodyText>
      </div>

      {/* Auto-save indicator */}
      {lastSaved && (
        <div className={cn(
          'text-xs flex items-center gap-2 px-3 py-1 rounded-full w-fit',
          isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600'
        )}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Auto-saved at {lastSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="max-w-4xl mx-auto">
        {/* Patient Information Form */}
        <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          {/* Patient Name */}
          <FormField
            label="Patient Name"
            required
            error={showValidationErrors ? (validationErrors.patientName || errors.patientName) : errors.patientName}
            fullWidth
          >
            <Input
              ref={(el) => { fieldRefs.current.patientName = el; }}
              type="text"
              value={localData.patientName || ''}
              onChange={(e) => handleFieldChange('patientName', e.target.value)}
              placeholder="Enter patient's full name"
              disabled={isLoading}
              maxLength={100}
            />
          </FormField>

          {/* Email Address */}
          <FormField
            label="Email Address"
            required
            error={showValidationErrors ? (validationErrors.email || errors.email) : errors.email}
            fullWidth
          >
            <Input
              ref={(el) => { fieldRefs.current.email = el; }}
              type="email"
              value={localData.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="patient@example.com"
              disabled={isLoading}
            />
          </FormField>

          {/* File Upload Section */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">
                Referral Letter (Optional)
              </Label>
              <BodyText variant="secondary" size="sm" className="mt-1">
                Upload relevant medical documents or other supporting files (PDF, DOC, DOCX)
              </BodyText>
            </div>

            <FileSelect
              onFileSelect={handleFileUpload}
              onValidate={handleFileValidation}
              acceptedTypes={['.pdf', '.doc', '.docx']}
              maxSize={10 * 1024 * 1024} // 10MB
              disabled={isLoading}
              className="w-full"
              showDescription={false}
              placeholder="Click to select or drag and drop"
            />

            {/* Local Documents List */}
            {localData.documents && localData.documents.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Selected Documents</Label>
                <div className="space-y-2">
                  {localData.documents.map((doc, index) => (
                    <div
                      key={`${doc.filename}-${index}`}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border',
                        isDark ? 'bg-secondary-800 border-secondary-700' : 'bg-secondary-50 border-secondary-200'
                      )}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={cn(
                          'flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center',
                          isDark ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'
                        )}>
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <BodyText className="font-medium">{doc.filename}</BodyText>
                          <BodyText variant="secondary" size="sm">
                            {(doc.size / 1024 / 1024).toFixed(2)} MB
                            {doc.description && ` • ${doc.description}`}
                          </BodyText>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          // Remove document from local storage
                          const updatedDocuments = localData.documents?.filter((_, i) => i !== index) || [];
                          const newData = { ...localData, documents: updatedDocuments };
                          setLocalData(newData);
                          setIsDirty(true);
                        }}
                        variant="outline"
                        size="sm"
                        className={cn(
                          'text-error-600 border-error-300 hover:bg-error-50',
                          isDark && 'text-error-400 border-error-600 hover:bg-error-900'
                        )}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>




          {/* Health Conditions Section */}
          <FormField
            label="Health Conditions"
            required
            error={showValidationErrors ? (validationErrors.conditions || errors.conditions) : errors.conditions}
            helperText="Select all conditions that apply to this patient"
            fullWidth
          >
            <HealthConditionsSelector
              ref={(el) => { fieldRefs.current.conditions = el; }}
              selectedConditions={localData.conditions || []}
              onConditionsChange={(conditions: string[]) => handleFieldChange('conditions', conditions)}
              error={showValidationErrors ? (validationErrors.conditions || errors.conditions) : errors.conditions}
              disabled={isLoading}
            />
          </FormField>

          {/* Additional Notes */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Additional Notes (Optional)
            </Label>
            <TextArea
              ref={(el) => { fieldRefs.current.additionalNotes = el; }}
              value={localData.additionalNotes || ''}
              onChange={(e) => handleFieldChange('additionalNotes', e.target.value)}
              placeholder="Any additional information about the patient's condition or requirements..."
              disabled={isLoading}
              rows={3}
              maxLength={500}
            />
            <BodyText variant="muted" size="sm" className="mt-1">
              {(localData.additionalNotes || '').length}/500 characters
            </BodyText>
          </div>
        </form>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={true}
        >
          Back
        </Button>

        <Button
          onClick={handleNext}
          disabled={isLoading || isValidating}
        >
          {isLoading ? 'Loading...' : isValidating ? 'Validating...' : 'Next: Select Dietitian'}
        </Button>
      </div>

      {/* Error Display */}
      {(showValidationErrors && (Object.keys(validationErrors).length > 0 || Object.keys(errors).length > 0)) || uploadError && (
        <Card className={cn(
          'p-4 border-l-4 border-error-500',
          isDark ? 'bg-error-900/20' : 'bg-error-50'
        )}>
          <BodyText variant="primary" className="font-medium mb-2">
            Please correct the following errors:
          </BodyText>
          <ul className="space-y-1">
            {Object.values({ ...errors, ...validationErrors }).map((error, index) => (
              <li key={index}>
                <BodyText variant="secondary" size="sm">• {error}</BodyText>
              </li>
            ))}
            {uploadError && (
              <li>
                <BodyText variant="secondary" size="sm">• {uploadError}</BodyText>
              </li>
            )}
          </ul>
        </Card>
      )}
    </div>
  );
};